import { useEffect, useMemo, useState } from 'react';
import { api } from '@/services/api';
import { StatBarChart } from '@/components/analytics/StatBarChart';
import type { BrandCoverageEntry, CoverageStatus } from '@/types/domain';

const AUDIENCE_LABEL: Record<string, string> = {
  women: 'Women',
  men: 'Men',
  child: 'Youth',
};

const CATEGORY_LABEL: Record<string, string> = {
  tops: 'Tops',
  bottoms: 'Bottoms',
};

const STATUS_LABEL: Record<CoverageStatus, string> = {
  verified: 'Verified',
  pending: 'Pending verification',
  locked: 'Locked',
};

const STATUS_CLASS: Record<CoverageStatus, string> = {
  verified: 'bg-brand/10 text-brand',
  pending: 'bg-stone-100 text-muted',
  locked: 'bg-danger/10 text-danger',
};

interface BrandGroup {
  brandName: string;
  brandSlug: string;
  entries: BrandCoverageEntry[];
}

export function BrandCoveragePage() {
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const [groups, setGroups] = useState<BrandGroup[]>([]);
  const [allEntries, setAllEntries] = useState<BrandCoverageEntry[]>([]);

  const statusCounts = useMemo(() => {
    const counts: Record<CoverageStatus, number> = { verified: 0, pending: 0, locked: 0 };
    for (const entry of allEntries) counts[entry.status] += 1;
    return counts;
  }, [allEntries]);

  useEffect(() => {
    let cancelled = false;
    api.getCoverage().then((res) => {
      if (cancelled) return;
      if (!res.success) {
        setStatus('error');
        return;
      }
      const byBrand = new Map<string, BrandGroup>();
      for (const entry of res.data) {
        const existing = byBrand.get(entry.brandSlug);
        if (existing) {
          existing.entries.push(entry);
        } else {
          byBrand.set(entry.brandSlug, { brandName: entry.brandName, brandSlug: entry.brandSlug, entries: [entry] });
        }
      }
      setGroups([...byBrand.values()].sort((a, b) => a.brandName.localeCompare(b.brandName)));
      setAllEntries(res.data);
      setStatus('success');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Brand coverage</h2>
        <p className="mt-1 text-sm text-muted">
          What we've verified so far, by brand, audience, and garment category. A brand only appears as
          selectable in a comparison once its chart is verified — everything else is shown here as pending
          so you know what's coming.
        </p>
      </div>

      {status === 'loading' && <p className="text-sm text-muted">Loading coverage…</p>}
      {status === 'error' && (
        <p role="alert" className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
          Couldn't load brand coverage right now. Please try again.
        </p>
      )}

      {status === 'success' && (
        <div className="space-y-4">
          <StatBarChart
            title={`Coverage overview — ${allEntries.length} audience/category combinations across ${groups.length} brands`}
            rows={[
              { key: 'verified', label: 'Verified', value: statusCounts.verified, colorClass: 'bg-brand' },
              { key: 'pending', label: 'Pending verification', value: statusCounts.pending, colorClass: 'bg-stone-400' },
              { key: 'locked', label: 'Locked', value: statusCounts.locked, colorClass: 'bg-danger' },
            ]}
          />
          {groups.map((group) => (
            <div key={group.brandSlug} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-card">
              <p className="text-base font-semibold text-ink">{group.brandName}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {group.entries.map((entry) => (
                  <div
                    key={`${entry.audience}-${entry.category}`}
                    className="flex items-center justify-between gap-2 rounded-xl border border-stone-100 px-2.5 py-2 text-xs"
                  >
                    <span className="font-medium text-ink">
                      {AUDIENCE_LABEL[entry.audience]} · {CATEGORY_LABEL[entry.category]}
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 font-medium ${STATUS_CLASS[entry.status]}`}>
                      {STATUS_LABEL[entry.status]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
